"use client";

import React, { useEffect, useRef, useState } from "react";
import { ArrowRight, Activity, ShieldCheck, Stethoscope } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Link } from "@/components/common/Link";
type UseRevealOnScrollOptions = {
  once?: boolean;
  threshold?: number | number[];
  rootMargin?: string;
};
function useRevealOnScroll<T extends HTMLElement = HTMLElement>({
  once = true,
  threshold = 0.2,
  rootMargin = "0px"
}: UseRevealOnScrollOptions = {}) {
  const ref = useRef<T | null>(null);
  const [isVisible, setIsVisible] = useState(false);
  useEffect(() => {
    const node = ref.current;
    if (!node) {
      return;
    }
    if (typeof window === "undefined" || typeof IntersectionObserver === "undefined") {
      setIsVisible(true);
      return;
    }
    const observer = new IntersectionObserver(entries => {
      const entry = entries[0];
      if (entry?.isIntersecting) {
        setIsVisible(true);
        if (once) {
          observer.unobserve(entry.target);
        }
      } else if (!once) {
        setIsVisible(false);
      }
    }, {
      threshold,
      rootMargin
    });
    observer.observe(node);
    return () => {
      observer.disconnect();
    };
  }, [once, rootMargin, threshold]);
  return {
    ref,
    isVisible
  };
}
const careWings = [{
  title: "Family & Preventive Care",
  description: "Routine check-ups, immunizations, and wellness education tailored for every member of the Freetown Family Clinic community.",
  icon: ShieldCheck,
  link: "/ContactPage"
}, {
  title: "Chronic Health Support",
  description: "Dedicated management for long-term conditions, combining medical expertise with compassionate, personalized attention.",
  icon: Activity,
  link: "/ContactPage"
}, {
  title: "Pediatric & Women’s Health",
  description: "Specialized care for our youngest patients and comprehensive services supporting women through every stage of life.",
  icon: Stethoscope,
  link: "/ContactPage"
}];
export default function SpecializedCareWings() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.1
  });
  return <section data-section-id="135982" id="comprehensive-care-for-every-stage" className="relative overflow-hidden bg-background py-24 sm:py-32">
      {}
      <div className="pointer-events-none absolute inset-0 flex items-center justify-center opacity-30">
        <div className="h-[40rem] w-[40rem] rounded-full bg-primary/10 blur-3xl" />
        <div className="h-[30rem] w-[30rem] rounded-full bg-accent/20 blur-3xl" />
      </div>

      <div ref={ref} className={`container relative mx-auto px-4 transition-all duration-1000 ease-out ${isVisible ? "translate-y-0 opacity-100" : "translate-y-12 opacity-0"}`}>
        <div className="mx-auto max-w-2xl text-center">
          <Badge variant="secondary" className="mb-6 rounded-full px-4 py-1.5 text-xs font-mono tracking-widest text-secondary-foreground shadow-sm">
            TRUSTED COMMUNITY CARE
          </Badge>
          <h2 className="font-serif text-5xl font-bold tracking-tight text-foreground sm:text-6xl">
            Care for Every Stage
          </h2>
          <p className="mt-6 text-xl leading-relaxed text-muted-foreground">
            At Freetown Family Clinic, we blend clinical precision with community warmth to provide a sanctuary for your family's health and vitality.
          </p>
        </div>

        <div className="mx-auto mt-16 grid max-w-7xl grid-cols-1 gap-8 sm:mt-20 md:grid-cols-3">
          {careWings.map((wing, i) => {
          const Icon = wing.icon;
          return <div data-index={i} key={i} className="h-full">
                <Card className="group flex h-full flex-col rounded-[24px] border-border/50 bg-card/40 shadow-sm backdrop-blur-md transition-all duration-500 hover:-translate-y-[10px] hover:bg-card/80 hover:shadow-xl hover:shadow-primary/10 hover:border-primary/40">
                  <CardHeader className="pb-4">
                    <div className="mb-4 inline-flex size-14 items-center justify-center rounded-[24px] bg-primary/10 text-primary transition-colors duration-500 group-hover:bg-primary group-hover:text-primary-foreground">
                      <Icon className="size-6" />
                    </div>
                    <CardTitle className="text-2xl font-serif font-semibold text-foreground">
                      {wing.title}
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="flex flex-1 flex-col justify-between">
                    <CardDescription className="text-base leading-relaxed text-muted-foreground">
                      {wing.description}
                    </CardDescription>
                    <div className="mt-8">
                      <Link to={wing.link} className="inline-flex items-center text-sm font-semibold text-primary transition-colors hover:text-primary/80">
                        <ArrowRight className="ml-2 size-4 transition-transform duration-300 group-hover:translate-x-2" />
                      </Link>
                    </div>
                  </CardContent>
                </Card>
              </div>;
        })}
        </div>
      </div>
    </section>;
}